import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Alert, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { Splash } from './components/Splash';
import { PageLayout } from './components/PageLayout';
import { signUpTheme } from './theme/theme';
import { InitializationPage } from './pages/InitializationPage';
import { RegisterCallbackPage } from './pages/RegisterCallbackPage';
import { SignUpPage } from './pages/SignUpPage';
import { initializeMsalForSignUp } from './auth/msal';

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/signup" replace />} />
    <Route path="/signup" element={<SignUpPage />} />
    <Route path="/signup/:invitationId" element={<SignUpPage />} />
    <Route path="/signup/Register" element={<RegisterCallbackPage />} />
    <Route path="/initialization" element={<InitializationPage />} />
    <Route path="*" element={<Navigate to="/signup" replace />} />
  </Routes>
);

type BootstrapState =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error'; message: string };

export const App = () => {
  const [bootstrap, setBootstrap] = useState<BootstrapState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    initializeMsalForSignUp()
      .then((instance) => {
        if (cancelled) return;
        if (!instance) {
          setBootstrap({
            status: 'error',
            message: 'MSAL nicht initialisiert. VITE_SIGNUP_MAIN_APP_ID prüfen.',
          });
          return;
        }
        setBootstrap({ status: 'ready' });
      })
      .catch((error) => {
        if (cancelled) return;
        // handleRedirectPromise threw — typically because AAD bounced back
        // with an error (e.g. consent declined, URI mismatch). Surface the
        // message instead of looping.
        setBootstrap({
          status: 'error',
          message: error instanceof Error ? error.message : String(error),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (bootstrap.status === 'loading') return <Splash />;

  if (bootstrap.status === 'error') {
    return (
      <ThemeProvider theme={signUpTheme}>
        <CssBaseline />
        <PageLayout>
          <Typography variant="h5" gutterBottom>
            Bootstrap-Fehler
          </Typography>
          <Alert severity="error">{bootstrap.message}</Alert>
        </PageLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={signUpTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
};
